import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { LOGO_FULL } from "@/components/common";
import { COPY } from "./landing/copy";
import { track, LEAD_DONE } from "./landing/parts";
import { abTrack, useLandingImages } from "@/lib/ab";
import { CheckoutModal } from "./landing/Checkout";
import { LeadPopup } from "./landing/LeadPopup";

const POPUP_KEY = "sb_popup_state";
import { Hero, Problem, Different, Inside, Preview } from "./landing/SectionsTop";
import { Fit, Value, Who, Offer, Process, Faq, Final, StickyBar } from "./landing/SectionsBottom";

const initLang = () => localStorage.getItem("sb_landing_lang") || (navigator.language?.startsWith("fr") ? "fr" : "en");

function Header({ lang, setLang, t, cta }) {
  return (
    <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-[#E1ECEF] pt-safe">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-3">
        <img src={LOGO_FULL} alt="Sidebizz" className="h-8" data-testid="landing-logo" />
        <div className="flex items-center gap-3">
          <div className="flex rounded-full border border-[#D5E3E8] p-0.5 text-xs font-bold" data-testid="landing-lang-toggle">
            {["en", "fr"].map((l) => <button key={l} onClick={() => setLang(l)} className={`px-3 h-8 rounded-full uppercase ${lang === l ? "bg-[#102B3F] text-white" : "text-[#5C6B73]"}`} data-testid={`landing-lang-${l}`}>{l}</button>)}
          </div>
          <div className="hidden md:block"><button onClick={() => cta("header")} className="lp-btn min-h-[42px] px-5 text-sm" data-testid="landing-header-cta">{t.getGuide}</button></div>
        </div>
      </div>
    </header>
  );
}

export default function Landing() {
  const imgs = useLandingImages();
  const [lang, setLangState] = useState(initLang);
  const [offer, setOffer] = useState(null);
  const [ideas, setIdeas] = useState([]);
  const [facets, setFacets] = useState([]);
  const [open, setOpen] = useState(false);
  const [popupCfg, setPopupCfg] = useState(null);
  const [popup, setPopup] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const openRef = useRef(false);
  openRef.current = open;
  const t = COPY[lang];
  const setLang = (l) => { if (l === lang) return; localStorage.setItem("sb_landing_lang", l); setLangState(l); abTrack("language_changed", { lang: l, variant: "paid_original" }); };
  useEffect(() => {
    api.get("/public/offer").then((r) => setOffer(r.data));
    api.get("/public/landing-ideas").then((r) => setIdeas(r.data)).catch(() => {});
    api.get("/public/popup").then((r) => setPopupCfg(r.data)).catch(() => {});
    api.get("/public/idea-facets").then((r) => setFacets(r.data)).catch(() => {});
    if (!sessionStorage.getItem("sb_visit")) { sessionStorage.setItem("sb_visit", "1"); track("visit"); }
    abTrack("landing_page_view", { lang, variant: "paid_original" });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { document.documentElement.lang = lang; }, [lang]);
  useEffect(() => {
    if (!popupCfg?.enabled || sessionStorage.getItem(POPUP_KEY) || localStorage.getItem(LEAD_DONE)) return;
    const id = setTimeout(() => {
      if (openRef.current || sessionStorage.getItem(POPUP_KEY) || localStorage.getItem(LEAD_DONE)) return;
      sessionStorage.setItem(POPUP_KEY, "shown");
      setPopup(true);
      track("popup_impression", { capture: "delayed_popup" });
    }, Math.max(1, popupCfg.delay) * 1000);
    return () => clearTimeout(id);
  }, [popupCfg]);
  const dismissPopup = () => { setPopup(false); track("popup_dismiss", { capture: "delayed_popup" }); };
  const cta = () => {
    if (!sessionStorage.getItem(POPUP_KEY)) sessionStorage.setItem(POPUP_KEY, "cancelled");
    setPopup(false); setPrefill(null); track("cta_click"); abTrack("main_cta_click", { lang, variant: "paid_original" }); setOpen(true);
  };
  const popupSubmit = (f) => {
    track("popup_submit", { capture: "delayed_popup" });
    setPrefill({ full_name: f.first_name.trim(), whatsapp: f.whatsapp, capture: "delayed_popup" });
    setPopup(false); setOpen(true);
  };
  const p = { t, offer, cta };
  return (
    <div className="min-h-screen bg-white overflow-x-hidden pb-24 md:pb-0" data-testid="landing-page">
      <Header lang={lang} setLang={setLang} t={t} cta={cta} />
      <Hero {...p} idea={ideas[0]} heroImg={imgs ? imgs.welcome_hero : "loading"} />
      <Problem t={t} />
      <Different t={t} />
      <Inside t={t} />
      <Preview {...p} ideas={ideas} />
      <Fit t={t} facets={facets} />
      <Value t={t} />
      <Who t={t} />
      <Offer {...p} idea={ideas[0]} />
      <Process t={t} offer={offer} />
      <Faq t={t} />
      <Final {...p} />
      <StickyBar {...p} hidden={open || popup} />
      <CheckoutModal open={open} onOpenChange={setOpen} t={t} lang={lang} prefill={prefill} />
      {popupCfg && <LeadPopup open={popup && !open} onDismiss={dismissPopup} onSubmit={popupSubmit} offer={offer} t={t} cfg={popupCfg[lang]} />}
    </div>
  );
}
